import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, RotateCw, FlipHorizontal, FlipVertical, Crop, Check, ZoomIn, ZoomOut } from 'lucide-react';

const ImageEditor = ({ imageUrl, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isCropping, setIsCropping] = useState(false);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Crop state
  const [cropBox, setCropBox] = useState(null);
  const [dragInfo, setDragInfo] = useState(null);
  // Track the base canvas size (before zoom)
  const baseSizeRef = useRef({ w: 0, h: 0 });

  // Load image once
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      redraw(img, 0, false, false);
    };
    img.onerror = () => {
      // Retry without CORS if it fails (local blob URLs)
      const img2 = new Image();
      img2.onload = () => {
        imgRef.current = img2;
        redraw(img2, 0, false, false);
      };
      img2.src = imageUrl;
    };
    img.src = imageUrl;
  }, [imageUrl]);

  const redraw = useCallback((img, rot, fh, fv) => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    const is90 = rot % 180 !== 0;
    const w = is90 ? img.height : img.width;
    const h = is90 ? img.width : img.height;
    const maxW = Math.min(720, window.innerWidth - 40);
    const maxH = Math.min(520, window.innerHeight - 240);
    const s = Math.min(1, maxW / w, maxH / h);
    canvas.width = Math.round(w * s);
    canvas.height = Math.round(h * s);
    baseSizeRef.current = { w: canvas.width, h: canvas.height };
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rot * Math.PI) / 180);
    ctx.scale(fh ? -1 : 1, fv ? -1 : 1);
    ctx.drawImage(img, -img.width * s / 2, -img.height * s / 2, img.width * s, img.height * s);
    ctx.restore();
  }, []);

  const applyTransform = (rot, fh, fv) => {
    if (imgRef.current) redraw(imgRef.current, rot, fh, fv);
  };

  const handleRotate = () => {
    const r = (rotation + 90) % 360;
    setRotation(r);
    setZoomLevel(1);
    applyTransform(r, flipH, flipV);
  };
  const handleFlipH = () => { const v = !flipH; setFlipH(v); applyTransform(rotation, v, flipV); };
  const handleFlipV = () => { const v = !flipV; setFlipV(v); applyTransform(rotation, flipH, v); };
  const handleZoomIn = () => setZoomLevel(z => Math.min(z + 0.25, 3));
  const handleZoomOut = () => setZoomLevel(z => Math.max(z - 0.25, 0.5));

  // Start crop
  const startCrop = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsCropping(true);
    setZoomLevel(1); // Reset zoom during crop
    const m = 0.1;
    setCropBox({ x: canvas.width * m, y: canvas.height * m, w: canvas.width * (1 - 2 * m), h: canvas.height * (1 - 2 * m) });
  };

  const cancelCrop = () => { setIsCropping(false); setCropBox(null); setDragInfo(null); };

  // Pointer helpers
  const getPos = (e) => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    const cx = (e.touches ? e.touches[0].clientX : e.clientX);
    const cy = (e.touches ? e.touches[0].clientY : e.clientY);
    // Account for CSS zoom transform
    return { x: (cx - r.left) / zoomLevel, y: (cy - r.top) / zoomLevel };
  };

  const hitTest = (pos, box) => {
    if (!box) return null;
    const { x, y, w, h } = box;
    const hs = 18;
    if (Math.abs(pos.x - x) < hs && Math.abs(pos.y - y) < hs) return 'nw';
    if (Math.abs(pos.x - (x + w)) < hs && Math.abs(pos.y - y) < hs) return 'ne';
    if (Math.abs(pos.x - x) < hs && Math.abs(pos.y - (y + h)) < hs) return 'sw';
    if (Math.abs(pos.x - (x + w)) < hs && Math.abs(pos.y - (y + h)) < hs) return 'se';
    if (Math.abs(pos.y - y) < hs && pos.x > x + hs && pos.x < x + w - hs) return 'n';
    if (Math.abs(pos.y - (y + h)) < hs && pos.x > x + hs && pos.x < x + w - hs) return 's';
    if (Math.abs(pos.x - x) < hs && pos.y > y + hs && pos.y < y + h - hs) return 'w';
    if (Math.abs(pos.x - (x + w)) < hs && pos.y > y + hs && pos.y < y + h - hs) return 'e';
    if (pos.x > x + 5 && pos.x < x + w - 5 && pos.y > y + 5 && pos.y < y + h - 5) return 'move';
    return null;
  };

  const onDown = (e) => {
    if (!isCropping || !cropBox) return;
    e.preventDefault();
    const pos = getPos(e);
    const handle = hitTest(pos, cropBox);
    if (handle) setDragInfo({ handle, startPos: pos, startBox: { ...cropBox } });
  };

  const onMove = useCallback((e) => {
    if (!dragInfo || !cropBox) return;
    e.preventDefault();
    const pos = getPos(e);
    const dx = pos.x - dragInfo.startPos.x;
    const dy = pos.y - dragInfo.startPos.y;
    const sb = dragInfo.startBox;
    const cw = canvasRef.current?.width || 800;
    const ch = canvasRef.current?.height || 600;
    const min = 30;
    let { x, y, w, h } = sb;

    switch (dragInfo.handle) {
      case 'move': x += dx; y += dy; break;
      case 'nw': x += dx; y += dy; w -= dx; h -= dy; break;
      case 'ne': y += dy; w += dx; h -= dy; break;
      case 'sw': x += dx; w -= dx; h += dy; break;
      case 'se': w += dx; h += dy; break;
      case 'n': y += dy; h -= dy; break;
      case 's': h += dy; break;
      case 'w': x += dx; w -= dx; break;
      case 'e': w += dx; break;
      default: break;
    }
    if (w < min) w = min;
    if (h < min) h = min;
    if (x < 0) x = 0;
    if (y < 0) y = 0;
    if (x + w > cw) { if (dragInfo.handle === 'move') x = cw - w; else w = cw - x; }
    if (y + h > ch) { if (dragInfo.handle === 'move') y = ch - h; else h = ch - y; }
    setCropBox({ x, y, w, h });
  }, [dragInfo, cropBox, zoomLevel]);

  const onUp = () => setDragInfo(null);

  // Apply crop using drawImage (avoids CORS/tainted canvas issues)
  const applyCrop = () => {
    if (!cropBox || !canvasRef.current) { cancelCrop(); return; }
    const canvas = canvasRef.current;
    const { x, y, w, h } = cropBox;
    const rx = Math.round(x), ry = Math.round(y), rw = Math.round(w), rh = Math.round(h);
    if (rw < 20 || rh < 20) { cancelCrop(); return; }

    // Use drawImage(canvas, ...) to copy the cropped region — avoids getImageData CORS error
    const tmp = document.createElement('canvas');
    tmp.width = rw;
    tmp.height = rh;
    tmp.getContext('2d').drawImage(canvas, rx, ry, rw, rh, 0, 0, rw, rh);

    const cropped = new Image();
    cropped.onload = () => {
      imgRef.current = cropped;
      setRotation(0); setFlipH(false); setFlipV(false); setZoomLevel(1);
      redraw(cropped, 0, false, false);
      setIsCropping(false); setCropBox(null);
    };
    cropped.src = tmp.toDataURL('image/png');
  };

  const handleSave = () => {
    const img = imgRef.current;
    if (!img) return;
    const exportCanvas = document.createElement('canvas');
    const rot = rotation, fh = flipH, fv = flipV;
    const is90 = rot % 180 !== 0;
    exportCanvas.width = Math.round(is90 ? img.height : img.width);
    exportCanvas.height = Math.round(is90 ? img.width : img.height);
    const ctx = exportCanvas.getContext('2d');
    ctx.translate(exportCanvas.width / 2, exportCanvas.height / 2);
    ctx.rotate((rot * Math.PI) / 180);
    ctx.scale(fh ? -1 : 1, fv ? -1 : 1);
    ctx.drawImage(img, -img.width / 2, -img.height / 2, img.width, img.height);

    exportCanvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'edited_image.jpg', { type: 'image/jpeg' });
        onSave(file, URL.createObjectURL(blob));
      }
    }, 'image/jpeg', 0.92);
  };

  return (
    <div className="fixed inset-0 bg-black flex flex-col z-[60]" data-testid="image-editor"
      onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
      onTouchMove={onMove} onTouchEnd={onUp}
    >
      {/* Top Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-black/80">
        <button onClick={onCancel} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10" data-testid="editor-cancel-btn">
          <X size={22} className="text-white" />
        </button>
        <div className="flex items-center gap-0.5">
          <button onClick={handleRotate} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10" data-testid="editor-rotate-btn"><RotateCw size={20} className="text-white" /></button>
          <button onClick={handleFlipH} className={`w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 ${flipH ? 'bg-white/20' : ''}`} data-testid="editor-fliph-btn"><FlipHorizontal size={20} className="text-white" /></button>
          <button onClick={handleFlipV} className={`w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 ${flipV ? 'bg-white/20' : ''}`} data-testid="editor-flipv-btn"><FlipVertical size={20} className="text-white" /></button>
          {!isCropping ? (
            <button onClick={startCrop} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10" data-testid="editor-crop-btn"><Crop size={20} className="text-white" /></button>
          ) : (
            <button onClick={applyCrop} className="w-10 h-10 rounded-full flex items-center justify-center bg-[#D4AF37]" data-testid="editor-apply-crop-btn"><Check size={20} className="text-white" /></button>
          )}
          <button onClick={handleZoomIn} disabled={isCropping} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 disabled:opacity-30" data-testid="editor-zoomin-btn"><ZoomIn size={20} className="text-white" /></button>
          <button onClick={handleZoomOut} disabled={isCropping} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 disabled:opacity-30" data-testid="editor-zoomout-btn"><ZoomOut size={20} className="text-white" /></button>
        </div>
      </div>

      {isCropping && (
        <div className="text-center py-1 bg-[#D4AF37]/10">
          <span className="text-white/80 text-xs">Drag corners/edges to crop</span>
          <button onClick={cancelCrop} className="ml-3 text-xs text-white/50 underline">Cancel</button>
        </div>
      )}

      {/* Canvas with CSS zoom */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-auto" ref={containerRef}>
        <div className="relative inline-block" style={{
          touchAction: 'none',
          transform: isCropping ? 'none' : `scale(${zoomLevel})`,
          transformOrigin: 'center center',
          transition: 'transform 0.15s ease-out'
        }}>
          <canvas ref={canvasRef} className="block rounded-lg"
            onMouseDown={onDown} onTouchStart={onDown}
          />
          {/* Crop overlay */}
          {isCropping && cropBox && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute bg-black/50" style={{ top: 0, left: 0, right: 0, height: cropBox.y }} />
              <div className="absolute bg-black/50" style={{ top: cropBox.y + cropBox.h, left: 0, right: 0, bottom: 0 }} />
              <div className="absolute bg-black/50" style={{ top: cropBox.y, left: 0, width: cropBox.x, height: cropBox.h }} />
              <div className="absolute bg-black/50" style={{ top: cropBox.y, left: cropBox.x + cropBox.w, right: 0, height: cropBox.h }} />
              <div className="absolute border-2 border-white" style={{ left: cropBox.x, top: cropBox.y, width: cropBox.w, height: cropBox.h }}>
                <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/25" />
                <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/25" />
                <div className="absolute top-1/3 left-0 right-0 h-px bg-white/25" />
                <div className="absolute top-2/3 left-0 right-0 h-px bg-white/25" />
                <div className="absolute -top-1 -left-1 w-5 h-5" style={{ borderTop: '3px solid white', borderLeft: '3px solid white' }} />
                <div className="absolute -top-1 -right-1 w-5 h-5" style={{ borderTop: '3px solid white', borderRight: '3px solid white' }} />
                <div className="absolute -bottom-1 -left-1 w-5 h-5" style={{ borderBottom: '3px solid white', borderLeft: '3px solid white' }} />
                <div className="absolute -bottom-1 -right-1 w-5 h-5" style={{ borderBottom: '3px solid white', borderRight: '3px solid white' }} />
                <div className="absolute top-1/2 -left-0.5 w-1 h-6 -translate-y-1/2 bg-white rounded-full" />
                <div className="absolute top-1/2 -right-0.5 w-1 h-6 -translate-y-1/2 bg-white rounded-full" />
                <div className="absolute -top-0.5 left-1/2 h-1 w-6 -translate-x-1/2 bg-white rounded-full" />
                <div className="absolute -bottom-0.5 left-1/2 h-1 w-6 -translate-x-1/2 bg-white rounded-full" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom */}
      <div className="p-4 flex items-center justify-between">
        {zoomLevel !== 1 && <span className="text-white/60 text-sm">{Math.round(zoomLevel * 100)}%</span>}
        <div className="flex-1" />
        <button onClick={handleSave} className="w-14 h-14 rounded-full bg-[#D4AF37] flex items-center justify-center hover:bg-[#C5A059] shadow-lg" data-testid="editor-save-btn">
          <Check size={28} className="text-white" />
        </button>
      </div>
    </div>
  );
};

export default ImageEditor;
